import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Section } from "@/components/app/Primitives";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ResultBadge } from "@/components/engage/ResultBadge";

const AUTHORITY = [
  ["Run pre-flight / rule audit", "HUL Media Finance · ProPay Operations · Auditor (EY) · Administrator"],
  ["Edit plan, creative status, PO link, handles", "HUL Media Finance · ProPay Operations · Administrator"],
  ["Authorise pre-flight exception (mandatory reason)", "HUL Media Finance · Administrator"],
  ["Configure rule severity / thresholds", "Administrator · HUL Media Finance"],
  ["Approve deliverables (per line)", "Auditor (EY) · Administrator"],
  ["Release payment", "ProPay Operations · Administrator"],
];

export default function RulesConfig() {
  const [rules, setRules] = useState([]);
  const load = () => api.get("/rules").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const patch = async (code, body) => {
    try { await api.patch(`/rules/${code}`, body); toast.success(`${code} updated`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-8" data-testid="rules-config-page">
      <Section title="Deterministic rule catalogue" subtitle={`Rule set ${rules[0]?.version || ""} · severity and thresholds are configurable; financial rules never depend on generative AI.`}>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Rule</th><th className="px-4 py-2">Scope</th><th className="px-4 py-2">Severity</th><th className="px-4 py-2">Parameters</th><th className="px-4 py-2">Enabled</th></tr></thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.code} className="border-t border-slate-100" data-testid={`rule-row-${r.code}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-800">{r.code} · {r.name}</div><div className="text-xs text-slate-500 max-w-md">{r.description}</div></td>
                  <td className="px-4 py-3 capitalize text-slate-600">{r.scope}</td>
                  <td className="px-4 py-3">
                    <Select value={r.severity} onValueChange={(v) => patch(r.code, { severity: v })}>
                      <SelectTrigger className="h-8 w-40" data-testid={`rule-severity-${r.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="blocking">Blocking</SelectItem><SelectItem value="review">Review</SelectItem><SelectItem value="informational">Informational</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {Object.entries(r.params || {}).map(([k, val]) => (
                      <div key={k} className="flex items-center gap-2 text-xs"><span className="text-slate-500 font-mono">{k}</span>
                        {typeof val === "boolean" ? <Switch checked={val} onCheckedChange={(v) => patch(r.code, { params: { ...r.params, [k]: v } })} data-testid={`rule-param-${r.code}-${k}`} />
                          : <Input className="h-7 w-24 font-mono text-xs" defaultValue={val} onBlur={(e) => Number(e.target.value) !== val && patch(r.code, { params: { ...r.params, [k]: Number(e.target.value) } })} data-testid={`rule-param-${r.code}-${k}`} />}
                      </div>
                    ))}
                    {!Object.keys(r.params || {}).length && <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3"><Switch checked={r.enabled} onCheckedChange={(v) => patch(r.code, { enabled: v })} data-testid={`rule-enabled-${r.code}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Authority matrix" subtitle="Who may take which material action. Configurable item — HUL to confirm final delegation of authority.">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Action</th><th className="px-4 py-2">Authorised roles</th><th className="px-4 py-2">Audit</th></tr></thead>
            <tbody>{AUTHORITY.map(([a, r]) => <tr key={a} className="border-t border-slate-100"><td className="px-4 py-2.5 text-slate-800">{a}</td><td className="px-4 py-2.5 text-slate-600">{r}</td><td className="px-4 py-2.5"><ResultBadge value="PASS" /></td></tr>)}</tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
